import { type SxProps, type Theme } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';
import { type To, matchPath } from 'react-router-dom';

import { theme } from '@shared/theme/theme';

// same size in the mini rail and the expanded list, so icons do not
// rescale when the sidebar is toggled
export const SIDEBAR_ICON_SIZE = 22;
// second-level links carry slightly smaller icons than their parents
export const SIDEBAR_CHILD_ICON_SIZE = 18;

const { colors } = theme;

export const getToPathname = (to: To): string | undefined => {
  const pathname = typeof to === 'string' ? to : to.pathname;
  // string paths may carry a search part (e.g. '/calendar?...')
  return pathname?.split('?')[0];
};

export const isPathActive = (to: To, currentPathname: string): boolean => {
  const pathname = getToPathname(to);
  return (
    pathname != null && matchPath(pathname + '/*', currentPathname) != null
  );
};

/**
 * Rounded-rectangle look shared by all sidebar navigation elements.
 * `collapsed` renders the icon-only square used in the mini rail.
 */
// returns a plain style object (not SxProps) so call sites can compose it
// inside `sx={[...]}` arrays
export const navItemSx = (_collapsed: boolean): SystemStyleObject<Theme> => ({
  borderRadius: '8px',
  mx: 1,
  my: '1px',
  // 9px + 22px icon + 9px fills the 40px square exactly, and because the
  // padding is identical in both modes the icon never moves horizontally
  // while the drawer width animates — the text is simply clipped away
  px: '9px',
  // identical 40px row in both modes so the rail and the expanded list
  // have exactly the same total height; zero py so the default button
  // padding cannot stretch rows past minHeight
  py: 0,
  minHeight: 40,
  justifyContent: 'flex-start',
  overflow: 'hidden',
  color: colors.mediumText,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    '.dark &': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: colors.blue50,
    color: colors.blue600,
  },
});

/**
 * Second-level link, both in the expanded accordion and in the
 * collapsed-mode popover.
 */
export const childItemSx = (variant: 'accordion' | 'popover'): SxProps<Theme> =>
  ({
    borderRadius: '8px',
    mx: 1,
    my: '1px',
    // zero py so the default button padding cannot stretch the row
    // past minHeight (same trick as navItemSx)
    py: 0,
    minHeight: 32,
    px: 0.75,
    // the hover surface spans the full row like the top-level items;
    // only the content (icon + label) is indented
    ...(variant === 'accordion' && { pl: 2.25 }),
    color: colors.mediumText,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      '.dark &': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
      },
    },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: colors.blue50,
      color: colors.blue600,
    },
  } as SxProps<Theme>);
